import { useEffect, useState, type FormEvent } from 'react'
import QRCode from 'qrcode'
import { Alert, Button, Field } from './ui'
import { useConfirmMfaSetup, useStartMfaSetup } from '../lib/profile'

export default function TwoFactorEnrollment({
  onStarted,
  onDone,
}: {
  onStarted: () => void
  onDone: () => void
}) {
  const start = useStartMfaSetup()
  const confirm = useConfirmMfaSetup()
  const [code, setCode] = useState('')
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)

  const authenticatorUri = start.data?.authenticatorUri

  useEffect(() => {
    if (authenticatorUri === undefined) {
      return
    }

    let cancelled = false

    // QR genere localement : l'URI contient la cle secrete.
    void QRCode.toDataURL(authenticatorUri, { margin: 1, width: 220 }).then((url) => {
      if (!cancelled) {
        setQrDataUrl(url)
      }
    })

    return () => {
      cancelled = true
    }
  }, [authenticatorUri])

  if (confirm.isSuccess) {
    return (
      <div className="space-y-4">
        <Alert tone="success">La verification en deux etapes est active.</Alert>
        <p className="text-sm text-neutral-700 dark:text-neutral-300">
          Voici vos codes de secours. Conservez-les hors de votre telephone : ils sont affiches une
          seule fois, et chacun permet une connexion si vous perdez votre application.
        </p>
        <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
          {confirm.data.recoveryCodes.map((recoveryCode) => (
            <li
              key={recoveryCode}
              className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-center dark:border-neutral-800 dark:bg-neutral-950"
            >
              {recoveryCode}
            </li>
          ))}
        </ul>
        <Button type="button" size="sm" onClick={onDone}>
          J'ai note mes codes
        </Button>
      </div>
    )
  }

  if (start.data === undefined) {
    return (
      <div className="space-y-3">
        {start.isError && <Alert>{start.error.message}</Alert>}
        <Button type="button" size="sm" disabled={start.isPending}
          onClick={() => start.mutate(undefined, { onSuccess: onStarted })}
        >
          {start.isPending ? 'Preparation...' : 'Activer avec Authenticator'}
        </Button>
      </div>
    )
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    confirm.mutate(code)
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-700 dark:text-neutral-300">
        Scannez ce code avec Google Authenticator, Microsoft Authenticator ou une application
        equivalente, puis saisissez le code a six chiffres affiche.
      </p>
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        {qrDataUrl !== null && (
          <img
            src={qrDataUrl}
            alt="Code QR d'enrolement de l'application d'authentification"
            className="rounded-lg bg-white p-2"
          />
        )}
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-950">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Ou saisissez cette cle</p>
          <p className="mt-1 font-mono text-sm tracking-wider text-neutral-900 dark:text-neutral-100">
            {start.data.sharedKey}
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="max-w-xs space-y-3">
        <Field
          label="Code a six chiffres"
          name="code"
          autoComplete="one-time-code"
          inputMode="numeric"
          required
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        {confirm.isError && <Alert>{confirm.error.message}</Alert>}
        <Button type="submit" size="sm" disabled={confirm.isPending}>
          {confirm.isPending ? 'Verification...' : 'Activer'}
        </Button>
      </form>
    </div>
  )
}
